import React, { useContext, useMemo } from "react";
import { routeGroups, RoutePaths } from "../enumStrings";
import { UserTypeContext } from "../contexts/UserTypeContext";
import useGetUserFromUserIdInAuth from "./useGetUserFromUserIdInAuth";
import { UserType } from "../graphql/types";
import { Route } from "react-router-dom";
import PageContainer from "../components/Layout/PageContainer";

type NavBarConfig = {
  title: string;
  path: string;
  icon: string;
};

type UserRoutesToShow = React.ReactElement<{
  path: string;
  key: string;
  element: React.ReactNode;
}>;

type FirstAllowedPathThatShouldBeConsideredTheHomePageForUser = string;

const maybeWrapWithTestingOverlay = (
  path: RoutePaths,
  element: React.ReactNode,
) => {
  // if (testingOverlayExcludedPaths.has(path)) {
  return element;
  // }

  // return <TestingInProgressOverlay>{element}</TestingInProgressOverlay>;
};

export const useGetLoggedInAppInfo = (): {
  navBarConfigToShow: NavBarConfig[] | undefined;
  userRoutesToShow: UserRoutesToShow[] | undefined;
  firstAllowedPathThatShouldBeConsideredTheHomePageForUser: FirstAllowedPathThatShouldBeConsideredTheHomePageForUser;
} => {
  const {
    state: { userType },
  } = useContext(UserTypeContext);

  const userInfo = useGetUserFromUserIdInAuth();

  const {
    navBarConfigToShow,
    userRoutesToShow,
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
  } = useMemo((): {
    navBarConfigToShow: NavBarConfig[] | undefined;
    userRoutesToShow: UserRoutesToShow[] | undefined;
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser: FirstAllowedPathThatShouldBeConsideredTheHomePageForUser;
  } => {
    if (
      userType !== UserType.ChamberAdmin
      // &&
      // userType !== UserType.ChamberMemberAdmin
    ) {
      return {
        navBarConfigToShow: [],
        userRoutesToShow: [],
        firstAllowedPathThatShouldBeConsideredTheHomePageForUser:
          RoutePaths.home,
      };
    }

    if (!userType || userType.length === 0) {
      return {
        navBarConfigToShow: [],
        userRoutesToShow: [],
        firstAllowedPathThatShouldBeConsideredTheHomePageForUser:
          RoutePaths.home,
      };
    }

    let navBarConfig: NavBarConfig[] = [];
    let resRoutes: UserRoutesToShow[] = [];

    const enabledPages =
      userInfo?.usagePermissions?.chamberMemberPro_EnabledPages || [];

    if (enabledPages.length > 0) {
      for (const routeGroup of routeGroups[userType]) {
        if (routeGroup.nestedRoute) {
          for (const route of routeGroup.nestedRoute.routes) {
            if (enabledPages.includes(route.pagePermissionNeeded)) {
              const wrappedElement = (
                <PageContainer
                  title={route.title}
                  description={route.description}
                >
                  {maybeWrapWithTestingOverlay(route.path, route.element)}
                </PageContainer>
              );

              resRoutes.push(
                <Route
                  path={route.path}
                  key={route.path}
                  element={wrappedElement}
                />,
              );
            }
          }
        }

        if (routeGroup.route) {
          if (enabledPages.includes(routeGroup.route.pagePermissionNeeded)) {
            const wrappedElement = (
              <PageContainer
                title={routeGroup.route.title}
                description={routeGroup.route.description}
              >
                {maybeWrapWithTestingOverlay(
                  routeGroup.route.path,
                  routeGroup.route.element,
                )}
              </PageContainer>
            );

            resRoutes.push(
              <Route
                path={routeGroup.route.path}
                key={routeGroup.route.path}
                element={wrappedElement}
              />,
            );
          }
        }
      }
    }

    const chamberSettingsRoute = resRoutes.find(
      (route) =>
        route.props.path === RoutePaths.chamberAdmin_myChamber_chamberSettings
    );
    const firstAllowedPathThatShouldBeConsideredTheHomePageForUser: FirstAllowedPathThatShouldBeConsideredTheHomePageForUser =
      chamberSettingsRoute?.props.path || resRoutes[0]?.props.path || RoutePaths.home;

    return {
      navBarConfigToShow: navBarConfig,
      userRoutesToShow: resRoutes,
      firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
    };
  }, [userType, userInfo]);

  return {
    navBarConfigToShow,
    userRoutesToShow,
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
  };
};
